import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Receipt, MagnifyingGlass, CheckCircle, X } from "@phosphor-icons/react";

/**
 * AxonautQuotationPicker — button + modal to browse the last 20 Axonaut
 * quotations and pick one to link to the chantier being created/edited.
 *
 * Value: the selected quotation id (string) or null.
 * Emits both the id AND the full quote summary via `onPick` so the parent
 * can pre-fill the chantier name / display the number.
 */
export default function AxonautQuotationPicker({
  value,
  onChange,
  onPick,
  clientId = null,
  testidPrefix = "axo-picker",
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    // When a clientId is provided AND the user hasn't toggled "voir tous",
    // filter server-side by that client's Axonaut company_id.
    const params = { limit: 20 };
    if (clientId && !showAll) params.client_id = clientId;
    api
      .get("/quotations", { params })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open, clientId, showAll]);

  // Reset "show all" toggle whenever the dialog re-opens fresh
  useEffect(() => { if (!open) setShowAll(false); }, [open]);

  // Look up the summary for the current value so the pill shows even after
  // a page refresh (e.g. inside EditChantierDialog).
  useEffect(() => {
    if (!value) {
      setSelectedSummary(null);
      return;
    }
    // Try to find the summary in the currently loaded list — otherwise we
    // just show the id as-is.
    const hit = items.find((it) => String(it.id) === String(value));
    if (hit) setSelectedSummary(hit);
  }, [value, items]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(
      (it) =>
        String(it.number || "").toLowerCase().includes(s) ||
        String(it.client_name || "").toLowerCase().includes(s) ||
        String(it.title || "").toLowerCase().includes(s),
    );
  }, [items, q]);

  const pick = (it) => {
    setSelectedSummary(it);
    onChange?.(String(it.id));
    onPick?.(it);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    setSelectedSummary(null);
    onChange?.(null);
    onPick?.(null);
  };

  return (
    <div>
      <Label className="text-white/60 text-[10px] uppercase tracking-widest flex items-center gap-1">
        <Receipt size={11} weight="duotone" className="text-[#7CB2FF]" /> Devis Axonaut
        <span className="text-white/40 normal-case tracking-normal ml-1">(optionnel)</span>
      </Label>
      <div className="mt-1 flex gap-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`flex-1 h-10 rounded-xl border px-3 flex items-center gap-2 text-sm transition-all ${
            value
              ? "bg-[#005CFF]/10 border-[#005CFF]/30 hover:border-[#005CFF]/60"
              : "bg-white/5 border-white/10 hover:border-[#005CFF]/40 text-white/60"
          }`}
          data-testid={`${testidPrefix}-open`}
        >
          <Receipt
            size={14}
            weight="duotone"
            className={value ? "text-[#7CB2FF]" : "text-white/50"}
          />
          <span className="flex-1 text-left truncate">
            {value ? (
              selectedSummary ? (
                <>
                  <span className="font-mono">{selectedSummary.number || value}</span>
                  {selectedSummary.client_name && (
                    <span className="text-white/50"> · {selectedSummary.client_name}</span>
                  )}
                </>
              ) : (
                <span className="font-mono">Devis lié : {value}</span>
              )
            ) : (
              "Lier un devis Axonaut…"
            )}
          </span>
        </button>
        {value && (
          <button
            type="button"
            onClick={clear}
            className="w-10 h-10 rounded-xl bg-white/5 hover:bg-red-500/20 border border-white/10 flex items-center justify-center transition-colors shrink-0"
            data-testid={`${testidPrefix}-clear`}
            aria-label="Retirer le lien"
          >
            <X size={12} className="text-white/60" />
          </button>
        )}
      </div>
      <div className="text-[9px] text-white/40 mt-1">
        Utilisé pour l&apos;écran « Prévu vs Réalisé » — récupère les lignes du devis.
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl max-h-[85vh] overflow-hidden flex flex-col"
          data-testid={`${testidPrefix}-dialog`}
        >
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Receipt size={20} className="text-[#7CB2FF]" weight="duotone" />
              Choisir un devis Axonaut
            </DialogTitle>
          </DialogHeader>

          <div className="relative">
            <MagnifyingGlass
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher par numéro, client ou objet…"
              className="bg-white/5 border-white/10 h-10 pl-8"
              autoFocus
              data-testid={`${testidPrefix}-search`}
            />
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 -mx-1 px-1">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-14 rounded-xl bg-white/[0.03] border border-white/10 animate-pulse"
                />
              ))
            ) : filtered.length === 0 ? (
              <div className="text-sm text-white/60 py-6 text-center space-y-3" data-testid={`${testidPrefix}-empty`}>
                <div>
                  {items.length === 0 ? (
                    clientId && !showAll
                      ? "Aucun devis Axonaut pour ce client."
                      : "Aucun devis remonté depuis Axonaut."
                  ) : (
                    "Aucun résultat pour cette recherche."
                  )}
                </div>
                {clientId && !showAll && items.length === 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAll(true)}
                    className="text-[11px] uppercase tracking-widest text-[#7CB2FF] hover:text-white underline"
                    data-testid={`${testidPrefix}-show-all`}
                  >
                    Voir tous les devis récents
                  </button>
                )}
              </div>
            ) : (
              filtered.map((it) => {
                const active = value && String(value) === String(it.id);
                const isAccepte = it.state === "accepte";
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => pick(it)}
                    className={`w-full text-left rounded-xl p-3 border flex items-center gap-3 transition-all active:scale-[0.99] ${
                      active
                        ? "bg-[#005CFF]/15 border-[#005CFF]/50"
                        : "bg-white/[0.03] border-white/10 hover:border-[#005CFF]/40"
                    }`}
                    data-testid={`${testidPrefix}-item-${it.id}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-[#005CFF]/15 flex items-center justify-center shrink-0">
                      <Receipt size={16} className="text-[#7CB2FF]" weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-semibold truncate">
                          {it.number || `#${it.id}`}
                        </span>
                        <span
                          className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-md font-semibold ${
                            isAccepte
                              ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                              : "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                          }`}
                        >
                          {isAccepte ? "Accepté" : "Chiffrage"}
                        </span>
                      </div>
                      <div className="text-[11px] text-white/70 truncate font-medium">
                        {it.client_name || "Client inconnu"}
                      </div>
                      {(it.title || it.date) && (
                        <div className="text-[10px] text-white/45 truncate">
                          {it.title ? <span>{it.title}</span> : null}
                          {it.title && it.date ? <span className="text-white/30"> · </span> : null}
                          {it.date ? <span>{it.date}</span> : null}
                        </div>
                      )}
                    </div>
                    {active && (
                      <CheckCircle size={16} weight="fill" className="text-[#7CB2FF] shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div className="flex gap-2 pt-2 border-t border-white/5">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              className="flex-1 border border-white/10"
              data-testid={`${testidPrefix}-close`}
            >
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
